import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle, XCircle, Clock, ArrowLeft, Trophy, Home, Calendar, Users, CreditCard, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useMyEvents } from '@/hooks/useMyEvents';
import { toast } from 'sonner';
import EmbeddedAuth from '@/components/EmbeddedAuth';
import PaymentProcess from '@/components/PaymentProcess';
import { usePlatform } from '@/hooks/usePlatform';

const translateCategory = (cat: string | undefined, language: string): string => {
  if (!cat) return '';
  if (language === 'es') return cat;
  const map: Record<string, string> = {
    'masculino': "Men's", 'femenino': "Women's", 'mixto': 'Mixed',
  };
  return map[cat.toLowerCase()] || cat;
};

const translateSubcategory = (sub: string | undefined, language: string): string => {
  if (!sub) return '';
  if (language === 'es') return sub;
  const map: Record<string, string> = {
    '1era': 'Division 1', '2nda': 'Division 2', '3era': 'Division 3',
    '4ta': 'Division 4', '5ta': 'Division 5', '6ta': 'Division 6',
  };
  return map[sub.toLowerCase()] || sub;
};

const SplitPaymentPage = () => {
  const navigate = useNavigate();
  const { splitPaymentId } = useParams<{ splitPaymentId: string }>();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const { t, language } = useLanguage();
  const { refetch: refetchMyEvents } = useMyEvents(user?.id);
  const { isNative, isIOS, isAndroid } = usePlatform();
  
  const [splitPayment, setSplitPayment] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);

  // Prevent duplicate verification calls
  const hasAttemptedVerification = useRef(false);

  const paymentSuccess = searchParams.get('payment_success') === 'true';
  const paymentCancelled = searchParams.get('payment_cancelled') === 'true';
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (splitPaymentId && !authLoading) {
      fetchSplitPayment();
    }
  }, [splitPaymentId, user, authLoading]);

  // Trigger verification immediately when returning from Stripe — no auth needed
  useEffect(() => {
    if (
      paymentSuccess &&
      splitPaymentId &&
      !hasAttemptedVerification.current
    ) {
      hasAttemptedVerification.current = true;
      handlePaymentConfirmation();
    }
  }, [paymentSuccess, splitPaymentId]);

  const fetchSplitPayment = async () => {
    try {
      const { data, error } = await supabase
        .from('split_payments')
        .select('*')
        .eq('id', splitPaymentId)
        .single();

      if (error) throw error;
      setSplitPayment(data);
    } catch (err) {
      console.error('Error fetching split payment:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentConfirmation = async () => {
    setVerifying(true);
    setVerificationError(null);

    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1000, 2000, 3000];

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        console.log(`[SplitPayment] Verification attempt ${attempt + 1}/${MAX_RETRIES}`);
        
        const { data, error } = await supabase.functions.invoke('verify-split-payment', {
          body: { splitPaymentId, sessionId }
        });

        if (error) throw error;

        if (data?.error) {
          throw new Error(data.error);
        }

        // Success
        setPaymentCompleted(true);
        toast.success(t('splitPayment.paymentConfirmed'));
        await fetchSplitPayment();
        await refetchMyEvents();
        setVerifying(false);
        return;
      } catch (err) {
        console.error(`[SplitPayment] Attempt ${attempt + 1} failed:`, err);
        
        if (attempt < MAX_RETRIES - 1) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt]));
        }
      }
    }

    // All retries exhausted
    setVerifying(false);
    setVerificationError(
      language === 'es'
        ? 'No pudimos confirmar tu pago. Tu pago en Stripe fue exitoso, pero la verificación falló.'
        : 'We could not confirm your payment. Your Stripe payment was successful, but verification failed.'
    );
  };

  const handleRetryVerification = () => {
    hasAttemptedVerification.current = false;
    setVerificationError(null);
    handlePaymentConfirmation();
  };

  // Show verifying spinner immediately when returning from Stripe (no auth needed)
  if (paymentSuccess && verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md flex flex-col items-center justify-center py-12 space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <h2 className="text-xl font-semibold text-foreground">
            {language === 'es' ? 'Verificando pago...' : 'Verifying payment...'}
          </h2>
          <p className="text-muted-foreground text-center">
            {language === 'es' ? 'Por favor espera mientras confirmamos tu pago.' : 'Please wait while we confirm your payment.'}
          </p>
        </div>
      </div>
    );
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Only show login wall if NOT returning from a successful payment
  if (!user && !paymentSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center">{t('splitPayment.loginRequired')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground text-center mb-4">
              {t('splitPayment.loginToPayDesc')}
            </p>
            <EmbeddedAuth />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!splitPayment) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">{t('splitPayment.notFound')}</h2>
          </CardContent>
        </Card>
      </div>
    );
  }

  const regData = splitPayment.registration_data as any;
  const isExpired = new Date(splitPayment.expires_at) < new Date();
  const isCompleted = splitPayment.status === 'completed' || paymentCompleted;
  const isPending = splitPayment.status === 'pending_partner' && !isExpired;
  const timeLeft = Math.max(0, Math.floor((new Date(splitPayment.expires_at).getTime() - Date.now()) / (1000 * 60 * 60)));
  const minutesLeft = Math.max(0, Math.floor((new Date(splitPayment.expires_at).getTime() - Date.now()) / (1000 * 60)) % 60);

  const canPay = isPending && user.id === splitPayment.partner_user_id;

  // Build paymentData for PaymentProcess
  const paymentData = {
    eventId: regData?.eventId || splitPayment.event_id,
    mode: (regData?.modality === 'singles' ? 'individual' : 'parejas') as 'individual' | 'parejas',
    partner: regData?.player1Name || null,
    paymentMode: 'my_part' as 'my_part' | 'full_amount',
    amount: splitPayment.partner_amount,
  };

  // Verifying state — show spinner
  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md flex flex-col items-center justify-center py-12 space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <h2 className="text-xl font-semibold text-foreground">
            {language === 'es' ? 'Verificando pago...' : 'Verifying payment...'}
          </h2>
          <p className="text-muted-foreground text-center">
            {language === 'es' ? 'Por favor espera mientras confirmamos tu pago.' : 'Please wait while we confirm your payment.'}
          </p>
        </div>
      </div>
    );
  }

  // Verification error state — show retry button
  if (verificationError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-6 text-center space-y-4">
              <XCircle className="h-12 w-12 text-amber-600 mx-auto" />
              <h2 className="text-lg font-semibold text-amber-900">
                {language === 'es' ? 'Error al verificar el pago' : 'Payment verification error'}
              </h2>
              <p className="text-sm text-amber-800">{verificationError}</p>
              <Button onClick={handleRetryVerification} className="w-full" variant="default">
                <RefreshCw className="h-4 w-4 mr-2" />
                {language === 'es' ? 'Reintentar verificación' : 'Retry verification'}
              </Button>
              <p className="text-xs text-muted-foreground">
                {language === 'es'
                  ? 'Si el problema persiste, contacta al organizador del evento.'
                  : 'If the problem persists, contact the event organizer.'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Completed state — rich confirmation UI
  if (isCompleted) {
    const handleGoToMyEvents = () => {
      navigate('/', { replace: true, state: { activeTab: 'my-events', forceRefresh: true } });
    };

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md space-y-6">
          {/* Success header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {language === 'es' ? '¡Inscripción Completada!' : 'Registration Complete!'}
            </h2>
            <p className="text-muted-foreground">
              {language === 'es' 
                ? 'Ambos pagos han sido confirmados exitosamente.' 
                : 'Both payments have been confirmed successfully.'}
            </p>
          </div>

          {/* Event details card */}
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="text-center">
                  <Trophy className="h-12 w-12 text-green-600 mx-auto mb-2" />
                  <h3 className="text-lg font-semibold text-green-900">
                    {regData?.eventTitle || 'Evento'}
                  </h3>
                </div>

                <div className="border-t border-green-200 pt-4 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-green-800 font-medium flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {t('payment.modality')}:
                    </span>
                    <Badge variant="outline" className="border-green-300 text-green-800">
                      {regData?.modality === 'singles' ? t('payment.singles') : t('payment.doubles')}
                    </Badge>
                  </div>

                  {regData?.category && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-green-800 font-medium">{t('payment.category')}:</span>
                      <span className="text-green-700">{translateCategory(regData.category, language)}</span>
                    </div>
                  )}

                  {regData?.subcategory && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-green-800 font-medium">{t('payment.subcategory')}:</span>
                      <span className="text-green-700">{translateSubcategory(regData.subcategory, language)}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-green-800 font-medium">{t('splitPayment.invitedBy')}:</span>
                    <span className="text-green-700">{regData?.player1Name}</span>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-green-800 font-medium">{t('payment.partner')}:</span>
                    <span className="text-green-700">{regData?.player2Name || user.email}</span>
                  </div>

                  <div className="flex items-center justify-between text-sm pt-2">
                    <span className="text-green-800 font-medium flex items-center gap-2">
                      <CreditCard className="h-4 w-4" />
                      {language === 'es' ? 'Tu pago' : 'Your payment'}:
                    </span>
                    <Badge className="bg-green-600 text-white">${Number(splitPayment.partner_amount).toFixed(2)} MXN</Badge>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-green-800 font-medium">{language === 'es' ? 'Estado' : 'Status'}:</span>
                    <Badge className="bg-green-600 text-white">
                      {language === 'es' ? 'Completado' : 'Completed'}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Next steps card */}
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="text-sm text-primary">
                  <p className="font-medium mb-1">
                    {language === 'es' ? 'Próximos Pasos' : 'Next Steps'}
                  </p>
                  <p className="text-primary/80">
                    {language === 'es' 
                      ? 'Tu inscripción está confirmada. Revisa los detalles del evento en "Mis Eventos" para ver horarios, grupos y más información.'
                      : 'Your registration is confirmed. Check event details in "My Events" for schedules, groups, and more info.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA button */}
          <div className="flex flex-col gap-3 pt-4">
            <Button
              onClick={handleGoToMyEvents}
              className="w-full bg-green-600 hover:bg-green-700"
              size="lg"
            >
              <Home className="h-5 w-5 mr-2" />
              {language === 'es' ? 'Ver Mis Eventos' : 'View My Events'}
            </Button>
          </div>

          <p className="text-sm text-muted-foreground text-center">
            {language === 'es' 
              ? 'Tu inscripción ha sido guardada. Puedes ver los detalles en tu panel de eventos.'
              : 'Your registration has been saved. You can view details in your events panel.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-background flex items-center justify-center p-4"
      style={{ paddingTop: isNative && (isIOS || isAndroid) ? '60px' : undefined }}
    >
      <div className="w-full max-w-md space-y-6">
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="-ml-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t('common.back') || 'Volver'}
        </Button>

        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-foreground mb-2">
            {t('payment.paymentInfo')}
          </h2>
          <p className="text-muted-foreground">
            {t('payment.completePayment')}
          </p>
        </div>

        {/* Expired State */}
        {isExpired && splitPayment.status !== 'completed' && (
          <Card className="bg-muted border-border">
            <CardContent className="p-6 text-center">
              <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <p className="text-sm text-destructive">{t('splitPayment.expired')}</p>
            </CardContent>
          </Card>
        )}

        {/* Time remaining badge */}
        {isPending && (
          <div className="flex items-center justify-center gap-2 text-amber-600">
            <Clock className="h-5 w-5" />
            <span className="text-sm font-medium">
              {timeLeft > 0 ? `${timeLeft}h ${minutesLeft}m` : `${minutesLeft}m`} {t('splitPayment.remaining')}
            </span>
          </div>
        )}

        {/* Registration Summary */}
        <Card className="bg-muted border-border">
          <CardHeader>
            <CardTitle className="text-lg">{t('payment.registrationSummary')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">{t('payment.event')}:</span>
              <span className="font-medium">{regData?.eventTitle}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">{t('payment.modality')}:</span>
              <Badge variant="outline">
                {regData?.modality === 'singles' ? t('payment.singles') : t('payment.doubles')}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">{t('payment.category')}:</span>
              <span className="font-medium">{translateCategory(regData?.category, language)}</span>
            </div>
            {regData?.subcategory && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t('payment.subcategory')}:</span>
                <span className="font-medium">{translateSubcategory(regData?.subcategory, language)}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">{t('splitPayment.invitedBy')}:</span>
              <span className="font-medium">{regData?.player1Name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">{t('payment.partner')}:</span>
              <span className="font-medium">{regData?.player2Name || user.email}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">{t('payment.paymentType')}:</span>
              <Badge variant="secondary">{t('payment.splitPayment')}</Badge>
            </div>
            
            <div className="border-t pt-3 mt-3">
              <div className="flex justify-between items-center text-lg font-semibold">
                <span>{t('payment.yourPart')}:</span>
                <span className="text-green-600">${Number(splitPayment.partner_amount).toFixed(2)} MXN</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {t('payment.splitPaymentNote').replace('{name}', regData?.player1Name || t('payment.partner'))}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Payment Component — only show when partner can pay */}
        {canPay && (
          <PaymentProcess
            paymentData={paymentData}
            eventTitle={regData?.eventTitle || 'Evento'}
            splitPaymentId={splitPayment.id}
            onPaymentSuccess={() => {
              setPaymentCompleted(true);
              refetchMyEvents();
              toast.success(t('splitPayment.paymentConfirmed'));
            }}
            onPaymentError={(error) => console.error('Split payment error:', error)}
            onBack={() => window.history.back()}
          />
        )}

        {/* Payment cancelled banner */}
        {paymentCancelled && (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <p className="text-sm text-amber-800">{t('splitPayment.paymentCancelled')}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SplitPaymentPage;
